import Numeral from 'numeral';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { affixIncreasedTimeMs } from '../../../../../../shared/common/affixHelpers/affixIncreasedTimeMs';
import {
  DUNGEON_SCORE_ALT_FACTOR,
  DUNGEON_SCORE_MAIN_FACTOR,
  mythicScoreUseAlternateDungeon
} from '../../../../shared/enums';
import { Affixes } from '../../../../../../shared/types/keystoneAffixTypes';
import { getPathToRun } from '../../bundles/routes';
import { Link } from '../../../../../../shared/frontend/components/link';
import { ComplexTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { RunTimeTooltipContent } from '../../../../../../shared/frontend/components/presentational/runTimeTooltip';
import { TDungeonSummary } from '../../../../shared/types/raidTypes';
import cx from 'classnames';
import { t } from 'i18next';
import { TSeasonDef } from '../../../../../../shared/gameConfig/mythicPlus';

interface IMythicDungeonRunIconProps {
  runs: any;
  dungeon: TDungeonSummary;
  seasonInfo: TSeasonDef;
  isAlternate?: boolean;
  size?: 'x-small' | 'small' | 'medium';
  isSpaced?: boolean;
}

export function MythicDungeonRunIcon(props: IMythicDungeonRunIconProps) {
  const { runs, dungeon, seasonInfo, isAlternate, isSpaced = true } = props;

  const run = runs ? runs.find((r: any) => r.zoneId === dungeon.id) : null;

  if (!run) {
    return (
      <ComplexTooltip
        placement="top"
        className={cx('slds-text-align--center d-inline-block', {
          'rio-spaced-element--medium': isSpaced
        })}
        tooltip={
          <span className="slds-text-body--small">
            <p>{dungeon.name}</p>
            <p style={{ color: '#ff6600' }}>
              <i className="fa fa-exclamation-triangle" /> No Run Found
            </p>
          </span>
        }
      >
        <MythicDungeonLink {...props} season={seasonInfo.slug} />
      </ComplexTooltip>
    );
  }

  const scoreRate = isAlternate ? DUNGEON_SCORE_ALT_FACTOR : DUNGEON_SCORE_MAIN_FACTOR;

  const keystoneTimeMs = (dungeon.keystone_timer_ms ?? 0) + affixIncreasedTimeMs(run.affixes);
  return (
    <ComplexTooltip
      placement="top"
      className={cx('slds-text-align--center d-inline-block', {
        'rio-spaced-element--medium': isSpaced
      })}
      tooltip={
        <RunTimeTooltipContent
          className="slds-text-body--small"
          run={{
            clearTimeMs: run.clearTimeMs,
            keystoneTimeMs
          }}
          useShortFormat={false}
          prefix={
            <>
              <p>
                +{run.mythicLevel} {t(`raids:${dungeon.slug}.name`)}
              </p>
              {mythicScoreUseAlternateDungeon(seasonInfo.scoringSystem) && (
                <p>
                  {run.affixes.includes(Affixes.FORTIFIED) && 'Fortified'}
                  {run.affixes.includes(Affixes.TYRANNICAL) && 'Tyrannical'}
                </p>
              )}
            </>
          }
          suffix={
            run.score ? (
              mythicScoreUseAlternateDungeon(seasonInfo.scoringSystem) ? (
                <p>Rating {Numeral(run.score * scoreRate).format('0,0.0')}</p>
              ) : (
                <p>Score {Numeral(run.score).format('0,0.0')}</p>
              )
            ) : undefined
          }
        />
      }
    >
      <MythicDungeonLink {...props} season={seasonInfo.slug} />
    </ComplexTooltip>
  );
}

interface IMythicDungeonLinkProps extends Omit<IMythicDungeonRunIconProps, 'seasonInfo' | 'runs'> {
  run?: any;
  season: string;
  runs?: any[];
}

/**
 * MythicDungeonLink
 *
 * TODO: move this to a shared file if possible
 */
export function MythicDungeonLink(props: IMythicDungeonLinkProps) {
  const { dungeon, runs, season, run, size = 'medium' } = props;

  const specificRun = run ? run : runs?.find((r: any) => r.zoneId === dungeon.id);

  if (!specificRun) {
    return (
      <a
        href="#"
        className={cx(`slds-avatar rio-realm-link nohover slds-avatar--${size}`)}
        style={{
          position: 'relative',
          background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${asset(dungeon.icon_url)})`,
          backgroundSize: 'contain',
          cursor: 'default',
          borderColor: '#222',
          borderWidth: '1px',
          borderStyle: 'solid'
        }}
      >
        <div className="rio-text--shadow rio-dungeonrow-title">
          <span style={{ color: '#777' }}>{dungeon.short_name}</span>
        </div>
      </a>
    );
  }

  const remainingTimeMs =
    (dungeon.keystone_timer_ms ?? 0) + affixIncreasedTimeMs(specificRun.affixes) - specificRun.clearTimeMs;

  const gradientScale = remainingTimeMs >= 0 ? 0.4 : 0.9;
  return (
    <Link
      to={getPathToRun({
        season,
        keystone_run_id: specificRun.keystoneRunId,
        mythic_level: specificRun.mythicLevel,
        dungeon
      })}
      className={cx(`slds-avatar slds-avatar--${size} slds-text-align--center rio-realm-link nohover`)}
      style={{
        overflow: 'visible',
        position: 'relative',
        background:
          `linear-gradient(rgba(0, 0, 0, ${gradientScale}), rgba(0, 0, 0, ${gradientScale})), ` +
          `url(${asset(dungeon.icon_url)})`,
        backgroundSize: 'contain',
        borderColor: remainingTimeMs >= 0 ? '#666' : '#000',
        borderWidth: '1px',
        borderStyle: 'solid'
      }}
    >
      <div
        className={cx('rio-text--shadow rio-dungeonrow-title', {
          'rio-dungeonrow-title--small': size === 'small',
          'rio-dungeonrow-title--x-small': size === 'x-small'
        })}
      >
        <span
          style={{
            color: remainingTimeMs >= 0 ? '#ccc' : '#999'
          }}
        >
          {dungeon.short_name}
        </span>
      </div>
      <div
        className={cx('rio-text--shadow text-white rio-dungeonrow-level text-bold', {
          'rio-dungeonrow-level--small': size === 'small',
          'rio-dungeonrow-level--x-small': size === 'x-small'
        })}
      >
        {specificRun.mythicLevel}
      </div>
    </Link>
  );
}
